import {gsap} from "gsap";
import {KennethMustDie} from "../KennethMustDie";
import {KMD_SYMBOL_ANIMATIONS} from "../server/IKMDData";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";

export class KMDWinOverlays extends PIXI.Container {

    private readonly _ignites: PIXI.spine.Spine[][];

    constructor() {
        super();
        this.name = "KMDWinOverlays";

        this._ignites = [[], [], [], [], []];
    }

    public playAnimation(state: KMD_SYMBOL_ANIMATIONS, x: number, y: number, name: string, shake: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            item: PIXI.spine.Spine = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset(name));

        item.position.set(x, y);

        if (shake) {
            tl.to(item, {
                duration: 1 / 30,
                x: item.x + 1
            }, 0.0667);
            tl.to(item, {
                duration: 1 / 30,
                x: item.x
            });

            tl.to(item, {
                duration: 1 / 30,
                y: item.y + 1
            }, 0.2333);
            tl.to(item, {
                duration: 1 / 30,
                y: item.y
            });
        }

        tl.add([
            () => {
                this.addChild(item);
            },
            KennethMustDie.createTimelineForAnimation(item, 0, state, false)
        ], 0);
        tl.add(() => {
            if (item.parent) {
                this.removeChild(item);
            }
        });

        return tl;
    }

    public playXHolePotentialHighlightAnimation(x: number, y: number): gsap.core.Timeline {
        const duration: number = 0.2,
            item: PIXI.Sprite = new PIXI.Sprite(KennethMustDie.assetLoader.getTextures("Highlight_White_Glow_syms")[0]),
            tl: gsap.core.Timeline = gsap.timeline();

        item.position.set(x, y);
        item.blendMode = PIXI.BLEND_MODES.SCREEN;
        item.anchor.set(0.5);
        item.alpha = 0;

        tl.add(() => {
            this.addChild(item);
        }, 0);
        tl.to(item, {
            duration: duration,
            alpha: 1
        }, 0);

        tl.to(this, {
            duration: 0.2
        });

        tl.to(item, {
            duration: duration,
            alpha: 0
        });

        tl.add(() => {
            this.removeChild(item);
        });

        return tl;
    }

    public playPreWinAnimation(resizeLevel: number, x: number, y: number): gsap.core.Timeline {
        const duration: number = 0.2,
            preWin: PIXI.Sprite = new PIXI.Sprite(KennethMustDie.assetLoader.getTextures("effects/Highlight_White_Glow")[0]),
            tl: gsap.core.Timeline = gsap.timeline();
        let yScale: number = 1;

        if (resizeLevel === 1) {
            yScale = 0.67;
        } else if (resizeLevel === 2) {
            yScale = 0.5;
        }

        preWin.position.set(x, y);
        preWin.scale.set(1, yScale);
        preWin.blendMode = PIXI.BLEND_MODES.ADD;
        preWin.anchor.set(0.5);
        preWin.alpha = 0;

        tl.add(() => {
            this.addChild(preWin);
        }, 0);
        tl.to(preWin, {
            duration: duration,
            alpha: 1
        }, 0);

        tl.to(preWin, {
            duration: duration,
            alpha: 0
        }, duration);

        tl.add(() => {
            this.removeChild(preWin);
        });

        return tl;
    }

    public playIgniteAnimation(resizeLevel: number, x: number, y: number, reelId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            item: PIXI.spine.Spine = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("symIgnite"));
        let start: KMD_SYMBOL_ANIMATIONS = KMD_SYMBOL_ANIMATIONS.IGNITE_START,
            loop: KMD_SYMBOL_ANIMATIONS = KMD_SYMBOL_ANIMATIONS.IGNITE_LOOP;

        if (resizeLevel === 1) {
            start = KMD_SYMBOL_ANIMATIONS.IGNITE_START_HALF;
            loop = KMD_SYMBOL_ANIMATIONS.IGNITE_LOOP_HALF;
        } else if (resizeLevel === 2) {
            start = KMD_SYMBOL_ANIMATIONS.IGNITE_START_SEVENTY_FIVE;
            loop = KMD_SYMBOL_ANIMATIONS.IGNITE_LOOP_SEVENTY_FIVE;
        }

        item.position.set(x, y);
        this._ignites[reelId].push(item);

        tl.add([
            () => {
                this.addChild(item);
            },
            KennethMustDie.createTimelineForAnimation(item, 0, start, false)
        ], 0);
        tl.add(() => {
            item.autoUpdate = true;
            item.state.setAnimation(0, loop, true);
        });

        return tl;
    }

    public playHeartAnimation(state: string, x: number, y: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            item: PIXI.spine.Spine = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("spM1M2Heart"));

        item.position.set(x, y);

        tl.add([
            () => {
                this.addChild(item);
            },
            KennethMustDie.createTimelineForAnimation(item, 0, state, false)
        ], 0);
        tl.add(() => {
            if (item.parent) {
                this.removeChild(item);
            }
        });

        return tl;
    }

    public stopIgniteAnimation(reelId: number): void {
        this._ignites[reelId].forEach((val: PIXI.spine.Spine) => {
            val.state.clearTracks();

            this.removeChild(val);
        });

        this._ignites[reelId] = [];
    }

    public playLightSweepFrameHighlight(reelId: number, rowId: number): void {
        const tl: gsap.core.Timeline = gsap.timeline(),
            item: TimelineSprite = new TimelineSprite(KennethMustDie.assetLoader.getTextures("xhole/xholeWin_effect_"));
        let x: number = (reelId * KennethMustDie.SYMBOL_WIDTH) + 216,
            y: number = KennethMustDie.SPECIAL_REEL_TOP;

        if (rowId !== 3) {
            x = (reelId * KennethMustDie.SYMBOL_WIDTH + KennethMustDie.SYMBOL_WIDTH * 0.5);
            y = (KennethMustDie.MAIN_REELS_TOP + (rowId * KennethMustDie.SYMBOL_HEIGHT));
        }

        item.position.set(x, y);
        item.anchor.set(0.5);
        item.scale.set(2);

        tl.add([
            () => {
                this.addChild(item);
            },
            <gsap.core.Timeline><unknown>item.getAnimation()
        ], 0);
        tl.add(() => {
            if (item.parent) {
                this.removeChild(item);
            }
        });
    }

    public playLightSweepAnimation(state: string, reelId: number, rowId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            item: PIXI.spine.Spine = new PIXI.spine.Spine(KennethMustDie.assetLoader.getSpineAsset("lightSweep"));
        let x: number = (reelId * KennethMustDie.SYMBOL_WIDTH) + 216,
            y: number = KennethMustDie.SPECIAL_REEL_TOP;

        if (rowId !== 3) {
            x = (reelId * KennethMustDie.SYMBOL_WIDTH + KennethMustDie.SYMBOL_WIDTH * 0.5);
            y = (KennethMustDie.MAIN_REELS_TOP + (rowId * KennethMustDie.SYMBOL_HEIGHT));
        }

        item.position.set(x, y);

        tl.add([
            () => {
                this.addChild(item);
            },
            KennethMustDie.createTimelineForAnimation(item, 0, state, false)
        ], 0);
        tl.add(() => {
            if (item.parent) {
                this.removeChild(item);
            }
        });

        return tl;
    }

    // #298
    public playXNudgeMultiUpAnimation(reelId: number, symId: number, symY: number): void {
        const anim: TimelineSprite = new TimelineSprite(KennethMustDie.assetLoader.getTextures("xhole/win_effect/Sparkle_Win_Animation_"));
        anim.anchor.set(0.5);
        anim.position.set((reelId * KennethMustDie.SYMBOL_WIDTH) + (KennethMustDie.SYMBOL_WIDTH / 2), (symId * KennethMustDie.SYMBOL_HEIGHT) + symY);

        this.addChild(anim);
        anim.getAnimation(undefined, undefined, () => {
            this.removeChild(anim);
        });
    }
}